import React, { useState } from 'react';
import { SafeMarkdown } from '../../ui/SafeMarkdown.js';
import { Badge } from '../../ui/Badge.js';
import { Button } from '../../ui/Button.js';
import { DataTable, type DataTableColumn } from '../../ui/DataTable.js';
import { ChevronDown, ChevronRight, Check, X } from 'lucide-react';

/** Humanize a snake_case or camelCase key into a readable label */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format a number for display */
function formatNumber(n: number): string {
  if (Number.isInteger(n) && Math.abs(n) < 1e6) return n.toLocaleString();
  if (Math.abs(n) < 0.01 || Math.abs(n) >= 1e6) return n.toExponential(2);
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/**
 * Check if an array of objects is table-renderable.
 * Accepts arrays where items share enough keys — doesn't require an exact match.
 * Each item must share at least 60% of the union of all keys seen across all items.
 */
function isHomogeneousObjectArray(arr: unknown[]): arr is Record<string, unknown>[] {
  if (arr.length === 0 || arr.length > 500) return false;
  if (!arr.every((item) => typeof item === 'object' && item !== null && !Array.isArray(item))) return false;
  const allKeys = new Set<string>();
  for (const item of arr) {
    for (const key of Object.keys(item as Record<string, unknown>)) allKeys.add(key);
  }
  if (allKeys.size === 0) return false;
  // Every item must have at least 60% of the union keys
  const threshold = allKeys.size * 0.6;
  return arr.every((item) => Object.keys(item as Record<string, unknown>).length >= threshold);
}

/** Check if an object has all-numeric values */
function isAllNumericValues(obj: Record<string, unknown>): boolean {
  const vals = Object.values(obj);
  return vals.length > 0 && vals.every((v) => typeof v === 'number');
}

/** Check if an object has all-string values */
function isAllStringValues(obj: Record<string, unknown>): boolean {
  const vals = Object.values(obj);
  return vals.length > 0 && vals.every((v) => typeof v === 'string');
}

// ── Leaf renderers ──────────────────────────────────────────────

const PercentageBar: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-muted w-40 truncate">{label}</span>
    <div className="flex-1 h-3 bg-surface-alt rounded-full overflow-hidden">
      <div
        className="h-full bg-accent rounded-full transition-all"
        style={{ width: `${Math.min(Math.max(value * 100, 0), 100)}%` }}
      />
    </div>
    <span className="text-xs font-mono text-body w-12 text-right">{(value * 100).toFixed(0)}%</span>
  </div>
);

const BooleanBadge: React.FC<{ value: boolean }> = ({ value }) => (
  <Badge variant={value ? 'success' : 'danger'} icon={value ? <Check size={10} /> : <X size={10} />}>
    {value ? 'Yes' : 'No'}
  </Badge>
);

// ── Auto-table for homogeneous object arrays ────────────────────

const AutoTable: React.FC<{ rows: Record<string, unknown>[] }> = ({ rows }) => {
  // Union of all keys across rows, preserving first-seen order
  const columns = Array.from(
    rows.reduce((set, row) => {
      for (const key of Object.keys(row)) set.add(key);
      return set;
    }, new Set<string>()),
  );

  const renderCell = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return <span className="text-faint italic">—</span>;
    if (typeof value === 'boolean') return <BooleanBadge value={value} />;
    if (typeof value === 'number') {
      if (value > 0 && value <= 1) {
        return <span className="font-mono text-xs">{(value * 100).toFixed(0)}%</span>;
      }
      return <span className="font-mono text-xs">{formatNumber(value)}</span>;
    }
    return <span className="text-xs">{String(value)}</span>;
  };

  const tableColumns: DataTableColumn<Record<string, unknown>>[] = columns.map((col) => ({
    key: col,
    header: humanizeKey(col),
    render: (row) => renderCell(row[col]),
  }));

  return <DataTable columns={tableColumns} rows={rows} rowKey={(_row, index) => index} framed />;
};

// ── Numeric bars (for objects with all-number values) ───────────

const NumericBars: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const max = Math.max(...Object.values(data).map(Math.abs), 0.01);
  const allFractional = Object.values(data).every((v) => v >= 0 && v <= 1);

  return (
    <div className="space-y-1.5">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-xs text-muted w-40 truncate">{humanizeKey(key)}</span>
          <div className="flex-1 h-3 bg-surface-alt rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all"
              style={{ width: `${(Math.abs(value) / (allFractional ? 1 : max)) * 100}%` }}
            />
          </div>
          <span className="text-xs font-mono text-body w-14 text-right">
            {allFractional ? `${(value * 100).toFixed(0)}%` : formatNumber(value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Key-value pairs (for objects with all-string values) ────────

const KeyValuePairs: React.FC<{ data: Record<string, string> }> = ({ data }) => (
  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
    {Object.entries(data).map(([key, value]) => (
      <React.Fragment key={key}>
        <span className="text-muted font-medium">{humanizeKey(key)}</span>
        <span className="text-body">{value}</span>
      </React.Fragment>
    ))}
  </div>
);

// ── Collapsible section ─────────────────────────────────────────

const CollapsibleSection: React.FC<{
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ label, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        {label}
      </Button>
      {open && <div className="pl-5 pt-1 space-y-2">{children}</div>}
    </div>
  );
};

// ── Markdown prose block ────────────────────────────────────────

const markdownOverrides = {
  p: { props: { className: 'mb-2 leading-relaxed text-body text-sm' } },
  strong: { props: { className: 'font-semibold text-heading' } },
  ul: { props: { className: 'list-disc pl-5 mb-2 space-y-1 text-sm text-body' } },
  ol: { props: { className: 'list-decimal pl-5 mb-2 space-y-1 text-sm text-body' } },
  li: { props: { className: 'text-sm' } },
  code: { props: { className: 'bg-surface-alt px-1.5 py-0.5 rounded-sm text-xs font-mono text-heading' } },
};

const ProseBlock: React.FC<{ text: string }> = ({ text }) => (
  <div className="prose prose-sm dark:prose-invert max-w-none">
    <SafeMarkdown overrides={markdownOverrides}>{text}</SafeMarkdown>
  </div>
);

// ── Main recursive renderer ─────────────────────────────────────

interface AutoRendererProps {
  data: unknown;
  label?: string;
  depth?: number;
}

export const AutoRenderer: React.FC<AutoRendererProps> = ({ data, label, depth = 0 }) => {
  // null / undefined
  if (data === null || data === undefined) {
    return <span className="text-faint italic text-xs">—</span>;
  }

  // Boolean
  if (typeof data === 'boolean') {
    return <BooleanBadge value={data} />;
  }

  // Number
  if (typeof data === 'number') {
    if (data >= 0 && data <= 1 && label) {
      return <PercentageBar value={data} label={humanizeKey(label)} />;
    }
    return <span className="font-mono text-sm text-body">{formatNumber(data)}</span>;
  }

  // String
  if (typeof data === 'string') {
    if (data.length > 200) return <ProseBlock text={data} />;
    return <span className="text-sm text-body">{data}</span>;
  }

  // Array
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-faint italic text-xs">Empty list</span>;
    }

    // Array of strings → bullet list
    if (data.every((item) => typeof item === 'string')) {
      return (
        <ul className="list-disc pl-5 space-y-0.5">
          {data.map((item, i) => (
            <li key={i} className="text-sm text-body">
              {item}
            </li>
          ))}
        </ul>
      );
    }

    // Array of homogeneous objects → auto-table
    if (isHomogeneousObjectArray(data)) {
      return <AutoTable rows={data} />;
    }

    // Mixed array → numbered items, each recursed
    return (
      <div className="space-y-2 pl-2">
        {data.map((item, i) => (
          <div key={i} className="flex gap-2">
            <span className="text-faint text-xs mt-0.5">{i + 1}.</span>
            <div className="flex-1">
              <AutoRenderer data={item} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Object
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const entries = Object.entries(obj);

    if (entries.length === 0) {
      return <span className="text-faint italic text-xs">Empty</span>;
    }

    // All-numeric values → horizontal bars
    if (isAllNumericValues(obj)) {
      return <NumericBars data={obj as Record<string, number>} />;
    }

    // All-string values → key-value pairs
    if (isAllStringValues(obj)) {
      return <KeyValuePairs data={obj as Record<string, string>} />;
    }

    // Mixed object → collapsible sections for nested, inline for primitives
    const primitiveEntries = entries.filter(([, v]) => typeof v !== 'object' || v === null);
    const complexEntries = entries.filter(([, v]) => typeof v === 'object' && v !== null);

    return (
      <div className="space-y-2">
        {primitiveEntries.length > 0 && (
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {primitiveEntries.map(([key, value]) => (
              <React.Fragment key={key}>
                <span className="text-muted font-medium">{humanizeKey(key)}</span>
                <span className="text-body">
                  {typeof value === 'boolean' ? (
                    <BooleanBadge value={value} />
                  ) : typeof value === 'number' && value >= 0 && value <= 1 ? (
                    <span className="font-mono">{(value * 100).toFixed(0)}%</span>
                  ) : typeof value === 'number' ? (
                    <span className="font-mono">{formatNumber(value)}</span>
                  ) : value === null || value === undefined ? (
                    <span className="text-faint italic">—</span>
                  ) : (
                    String(value)
                  )}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}
        {complexEntries.map(([key, value]) => (
          <CollapsibleSection key={key} label={humanizeKey(key)} defaultOpen={depth < 1}>
            <AutoRenderer data={value} label={key} depth={depth + 1} />
          </CollapsibleSection>
        ))}
      </div>
    );
  }

  // Fallback
  return <span className="text-sm text-body">{String(data)}</span>;
};
